/**
 * Pipelines Admin View — List, create, edit, and run skill pipelines.
 */

import { html, nothing } from "lit";

// ────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────

export type PipelineEntry = {
  id: string;
  projectId: string | null;
  name: string;
  description: string;
  steps: Array<Record<string, unknown>>;
  inputs: unknown[];
  outputs: unknown[];
  isActive: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
};

export type PipelineRunEntry = {
  id: string;
  pipelineId: string | null;
  projectId: string;
  status: string;
  inputs: Record<string, unknown>;
  outputs: Record<string, unknown>;
  error: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
};

export type PipelineStepRunEntry = {
  id: string;
  pipelineRunId: string;
  stepId: string;
  skillName: string;
  status: string;
  inputs: Record<string, unknown>;
  outputs: Record<string, unknown>;
  error: string | null;
  attempt: number;
  startedAt: string | null;
  completedAt: string | null;
};

export type PipelinesAdminProps = {
  pipelines: PipelineEntry[];
  loading: boolean;
  error: string | null;
  createMode: boolean;
  editingId: string | null;
  form: Partial<PipelineEntry> | null;
  runResult: {
    runId: string;
    status: string;
    outputs: Record<string, unknown>;
    error?: string;
  } | null;
  runLoading: boolean;
  runStatusResult: { run: PipelineRunEntry | null; steps: PipelineStepRunEntry[] } | null;
  projects: Array<{ id: string; name: string }>;
  filterProjectId: string | null;
  onRefresh: () => void;
  onStartCreate: () => void;
  onStartEdit: (id: string) => void;
  onCancelForm: () => void;
  onUpdateForm: (field: string, value: unknown) => void;
  onSave: () => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string, active: boolean) => void;
  onRun: (id: string) => void;
  onCheckStatus: (runId: string) => void;
  onFilterProject: (projectId: string | null) => void;
};

// ────────────────────────────────────────────────────────────
// Render
// ────────────────────────────────────────────────────────────

export function renderPipelinesAdmin(props: PipelinesAdminProps) {
  return html`
    <div style="display:flex;flex-direction:column;gap:16px;">
      <!-- Header -->
      <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
        <button @click=${props.onRefresh} ?disabled=${props.loading}>Refresh</button>
        <button @click=${props.onStartCreate} ?disabled=${props.createMode}>+ New Pipeline</button>
        <select @change=${(e: Event) => {
          const val = (e.target as HTMLSelectElement).value;
          props.onFilterProject(val || null);
        }}>
          <option value="">All Projects</option>
          ${props.projects.map(
            (p) =>
              html`<option value=${p.id} ?selected=${props.filterProjectId === p.id}>${p.name}</option>`,
          )}
        </select>
        ${
          props.loading
            ? html`
                <span style="opacity: 0.6">Loading...</span>
              `
            : nothing
        }
      </div>

      ${props.error ? html`<div style="color:var(--error,red);">${props.error}</div>` : nothing}

      <!-- Form (create/edit) -->
      ${props.form ? renderForm(props) : nothing}

      <!-- List -->
      ${
        props.pipelines.length === 0 && !props.loading
          ? html`
              <p style="opacity: 0.6">No pipelines found.</p>
            `
          : html`
            <div style="display:flex;flex-direction:column;gap:8px;">
              ${props.pipelines.map((p) => renderPipelineCard(p, props))}
            </div>
          `
      }

      <!-- Run result -->
      ${props.runResult ? renderRunResult(props) : nothing}

      <!-- Run status detail -->
      ${props.runStatusResult?.run ? renderRunStatus(props) : nothing}
    </div>
  `;
}

function renderForm(props: PipelinesAdminProps) {
  const form = props.form!;
  const isEdit = !!props.editingId;
  return html`
    <div style="border:1px solid var(--border,#444);padding:12px;border-radius:6px;">
      <h3 style="margin:0 0 8px;">${isEdit ? "Edit Pipeline" : "New Pipeline"}</h3>
      <div style="display:flex;flex-direction:column;gap:8px;">
        <label>
          Name
          <input
            type="text"
            .value=${form.name ?? ""}
            @input=${(e: Event) => props.onUpdateForm("name", (e.target as HTMLInputElement).value)}
            style="width:100%;box-sizing:border-box;"
          />
        </label>
        <label>
          Description
          <textarea
            .value=${form.description ?? ""}
            @input=${(e: Event) => props.onUpdateForm("description", (e.target as HTMLTextAreaElement).value)}
            rows="2"
            style="width:100%;box-sizing:border-box;"
          ></textarea>
        </label>
        <label>
          Steps (JSON)
          <textarea
            .value=${JSON.stringify(form.steps ?? [], null, 2)}
            @input=${(e: Event) => {
              try {
                const parsed = JSON.parse((e.target as HTMLTextAreaElement).value);
                props.onUpdateForm("steps", parsed);
              } catch {
                /* ignore parse errors while typing */
              }
            }}
            rows="8"
            style="width:100%;box-sizing:border-box;font-family:monospace;font-size:12px;"
          ></textarea>
        </label>
        <label>
          Inputs (JSON)
          <textarea
            .value=${JSON.stringify(form.inputs ?? [], null, 2)}
            @input=${(e: Event) => {
              try {
                const parsed = JSON.parse((e.target as HTMLTextAreaElement).value);
                props.onUpdateForm("inputs", parsed);
              } catch {
                /* ignore */
              }
            }}
            rows="3"
            style="width:100%;box-sizing:border-box;font-family:monospace;font-size:12px;"
          ></textarea>
        </label>
        <label>
          Outputs (JSON)
          <textarea
            .value=${JSON.stringify(form.outputs ?? [], null, 2)}
            @input=${(e: Event) => {
              try {
                const parsed = JSON.parse((e.target as HTMLTextAreaElement).value);
                props.onUpdateForm("outputs", parsed);
              } catch {
                /* ignore */
              }
            }}
            rows="3"
            style="width:100%;box-sizing:border-box;font-family:monospace;font-size:12px;"
          ></textarea>
        </label>
        <div style="display:flex;gap:8px;">
          <button @click=${props.onSave}>${isEdit ? "Update" : "Create"}</button>
          <button @click=${props.onCancelForm}>Cancel</button>
        </div>
      </div>
    </div>
  `;
}

function renderPipelineCard(pipeline: PipelineEntry, props: PipelinesAdminProps) {
  const stepCount = Array.isArray(pipeline.steps) ? pipeline.steps.length : 0;
  return html`
    <div
      style="border:1px solid var(--border,#444);padding:10px;border-radius:6px;display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap;"
    >
      <div style="flex:1;min-width:200px;">
        <strong>${pipeline.name}</strong>
        <span style="opacity:0.6;margin-left:4px;">v${pipeline.version}</span>
        ${
          !pipeline.isActive
            ? html`
                <span style="color: var(--warning, orange); margin-left: 4px">(inactive)</span>
              `
            : nothing
        }
        <div style="opacity:0.8;font-size:0.9em;margin-top:2px;">${pipeline.description}</div>
        <div style="opacity:0.6;font-size:0.8em;margin-top:2px;">${stepCount} steps</div>
      </div>
      <div style="display:flex;gap:4px;flex-wrap:wrap;">
        <button @click=${() => props.onRun(pipeline.id)} title="Run pipeline">Run</button>
        <button @click=${() => props.onStartEdit(pipeline.id)} title="Edit pipeline">Edit</button>
        <button
          @click=${() => props.onToggleActive(pipeline.id, !pipeline.isActive)}
          title=${pipeline.isActive ? "Deactivate" : "Activate"}
        >
          ${pipeline.isActive ? "Deactivate" : "Activate"}
        </button>
        <button @click=${() => props.onDelete(pipeline.id)} title="Delete pipeline" style="color:var(--error,red);">
          Delete
        </button>
      </div>
    </div>
  `;
}

function renderRunResult(props: PipelinesAdminProps) {
  const r = props.runResult!;
  return html`
    <div style="border:1px solid var(--border,#444);padding:10px;border-radius:6px;">
      <h4 style="margin:0 0 4px;">Run Result</h4>
      <div>Status: <strong>${r.status}</strong></div>
      <div>Run ID: <code>${r.runId}</code></div>
      ${r.error ? html`<div style="color:var(--error,red);">Error: ${r.error}</div>` : nothing}
      ${
        Object.keys(r.outputs).length > 0
          ? html`<pre style="font-size:12px;overflow:auto;max-height:200px;">${JSON.stringify(r.outputs, null, 2)}</pre>`
          : nothing
      }
      <button @click=${() => props.onCheckStatus(r.runId)} style="margin-top:4px;">View Details</button>
    </div>
  `;
}

function renderRunStatus(props: PipelinesAdminProps) {
  const { run, steps } = props.runStatusResult!;
  if (!run) {
    return nothing;
  }
  return html`
    <div style="border:1px solid var(--border,#444);padding:10px;border-radius:6px;">
      <h4 style="margin:0 0 4px;">Run Details (${run.id.slice(0, 8)}...)</h4>
      <div>Status: <strong>${run.status}</strong></div>
      ${run.error ? html`<div style="color:var(--error,red);">Error: ${run.error}</div>` : nothing}
      ${
        steps.length > 0
          ? html`
            <table style="width:100%;margin-top:8px;font-size:0.85em;border-collapse:collapse;">
              <thead>
                <tr style="text-align:left;border-bottom:1px solid var(--border,#444);">
                  <th>Step</th>
                  <th>Skill</th>
                  <th>Status</th>
                  <th>Attempt</th>
                </tr>
              </thead>
              <tbody>
                ${steps.map(
                  (s: PipelineStepRunEntry) => html`
                    <tr style="border-bottom:1px solid var(--border-dim,#333);">
                      <td>${s.stepId}</td>
                      <td>${s.skillName}</td>
                      <td>${s.status}</td>
                      <td>${s.attempt}</td>
                    </tr>
                  `,
                )}
              </tbody>
            </table>
          `
          : nothing
      }
    </div>
  `;
}
